import { useEffect, useMemo } from 'react';
import { useAppConfiguration } from '../useAppConfiguration';
import { useConsumerSourceId } from '../useDataHooks';
import useConstant from '../useConstant';
import { useModulesStore } from '../useModules';
import useCarleadHashParam from '../useCarleadHashParam';
import { getBookingPageUxFeatures } from './helpers';

function useGetMarketingTrackingService() {
  const {
    tracking
  } = useModulesStore();
  return tracking.marketingTrackingService;
}

const trackLandingWrapper = (config, consumerSourceId, carleadHash, params, service) => {
  const {
    trackLandingPageType,
    appName,
    appVersion,
    sourceId: configSourceId,
    env
  } = config; // booking landing is tracked differently (with external tracking)

  const isBookingLanding = appName.startsWith('@wkda/') && trackLandingPageType === 'booking-handover' && !!carleadHash;

  if (trackLandingPageType) {
    var _window, _window$location;

    const trackingPayload = {
      pageType: trackLandingPageType,
      appver: appName.replace('@wkda/funnels-', '') + "@" + appVersion,
      appname: appName,
      source: consumerSourceId || configSourceId,
      ...params
    };
    return isBookingLanding ? service.trackExternal({
      externalType: 'carlead',
      externalId: carleadHash
    }, { ...trackingPayload,
      category: 'booking',
      action: 'booking-landing',
      uxFeatures: getBookingPageUxFeatures((_window = window) === null || _window === void 0 ? void 0 : (_window$location = _window.location) === null || _window$location === void 0 ? void 0 : _window$location.href)
    }) : service.trackLanding(trackingPayload);
  } else {
    if (env !== 'prod') console.warn('[Warning] - Track landing works only if trackLandingPageType is added to the funnel config');
  }
};

const trackLeadFieldChangedWrapper = (config, consumerSourceId, fieldName, method, service) => {
  const {
    appName,
    appVersion,
    sourceId: configSourceId
  } = config;
  return service.trackLeadFieldChanged({
    appver: appName.replace('@wkda/funnels-', '') + "@" + appVersion,
    appname: appName,
    source: consumerSourceId || configSourceId,
    leadFieldChanged: fieldName,
    method
  });
};

const trackExternalWrapper = function (config, consumerSourceId, externalType, externalId, category, action) {
  let additionalData = arguments.length > 6 && arguments[6] !== undefined ? arguments[6] : {};
  let service = arguments.length > 7 ? arguments[7] : undefined;
  const {
    appName,
    appVersion,
    sourceId: configSourceId
  } = config;
  return service.trackExternal({
    externalType,
    externalId
  }, {
    appver: appName.replace('@wkda/funnels-', '') + "@" + appVersion,
    appname: appName,
    action,
    category,
    source: consumerSourceId || configSourceId,
    ...additionalData
  });
};

export function useTrackLandingCallback(params) {
  const config = useAppConfiguration();
  const sourceId = useConsumerSourceId();
  const service = useGetMarketingTrackingService();
  const carleadHash = useCarleadHashParam();
  return () => trackLandingWrapper(config, sourceId, carleadHash, params, service);
}
export function useTrackLanding(params) {
  const config = useAppConfiguration();
  const sourceId = useConsumerSourceId();
  const carleadHash = useCarleadHashParam();
  const configConstant = useConstant(() => config);
  const sourceIdConstant = useConstant(() => sourceId);
  const paramsConstant = useConstant(() => params);
  const carleadHashConstant = useConstant(() => carleadHash);
  const service = useGetMarketingTrackingService();
  useEffect(() => {
    trackLandingWrapper(configConstant, sourceIdConstant, carleadHashConstant, paramsConstant, service);
  }, [configConstant, sourceIdConstant, carleadHashConstant, paramsConstant, service]);
}
export function useTrackLeadFieldChanged() {
  const config = useAppConfiguration();
  const sourceId = useConsumerSourceId();
  const service = useGetMarketingTrackingService();
  return (fieldName, method) => trackLeadFieldChangedWrapper(config, sourceId, fieldName, method, service);
}
export function useTrackExternal() {
  const config = useAppConfiguration();
  const sourceId = useConsumerSourceId();
  const service = useGetMarketingTrackingService();
  return useMemo(() => (externalType, externalId, category, action, additionalData) => trackExternalWrapper(config, sourceId, externalType, externalId, category, action, additionalData, service), [config, service, sourceId]);
}